// Seed data so `npm run dev` shows a working gym on first run, with no
// Supabase project and no credentials. Swap the adapter for the real one and
// this file becomes the fixture set for tests.

import { PLAN_KINDS } from '../lib/entitlements.js';
import { dateKeyInZone, addDays } from '../lib/recurrence.js';

// Must match the org's zone below. Seeding in UTC while the app asks "what
// day is it here" put the demo's bookings on a different day than the
// schedule showed them.
const ORG_TZ = 'America/Chicago';
const today = dateKeyInZone(new Date(), ORG_TZ);
const monthStart = `${today.slice(0, 7)}-01`;
const monthEnd = (() => {
  const [y, m] = today.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${today.slice(0, 7)}-${String(last).padStart(2, '0')}`;
})();

export const org = {
  id: 'org_1',
  name: 'Ironline Strength',
  timeZone: ORG_TZ,
  currency: 'USD',
  taxRateBps: 875,
};

export const locations = [
  { id: 'loc_1', orgId: 'org_1', name: 'Ironline — Riverside', timeZone: 'America/Chicago' },
];

export const staff = [
  { id: 'staff_1', orgId: 'org_1', name: 'Dana Okafor', role: 'owner', email: 'dana@ironline.test' },
  { id: 'staff_2', orgId: 'org_1', name: 'Marcus Villanueva', role: 'coach', email: 'marcus@ironline.test' },
  { id: 'staff_3', orgId: 'org_1', name: 'Priya Raghunathan', role: 'coach', email: 'priya@ironline.test' },
  { id: 'staff_4', orgId: 'org_1', name: 'Sam Boateng', role: 'front_desk', email: 'sam@ironline.test' },
];

export const plans = [
  {
    id: 'plan_unlimited', orgId: 'org_1', name: 'Unlimited', kind: PLAN_KINDS.RECURRING_UNLIMITED,
    amountCents: 17900, interval: 'month', programs: [], blockWhenPastDue: false, active: true,
  },
  {
    id: 'plan_3x', orgId: 'org_1', name: '3x / week', kind: PLAN_KINDS.RECURRING_CAPPED,
    amountCents: 13900, interval: 'month', limit: 3, limitPeriod: 'week',
    programs: [], blockWhenPastDue: false, active: true,
  },
  {
    id: 'plan_pack10', orgId: 'org_1', name: '10-class pack', kind: PLAN_KINDS.PACK,
    amountCents: 19000, credits: 10, expiryDays: 120, programs: [], active: true,
  },
  {
    id: 'plan_dropin', orgId: 'org_1', name: 'Drop-in', kind: PLAN_KINDS.DROP_IN,
    amountCents: 2500, credits: 1, expiryDays: 30, programs: [], active: true,
  },
  {
    id: 'plan_yoga', orgId: 'org_1', name: 'Yoga only', kind: PLAN_KINDS.RECURRING_UNLIMITED,
    amountCents: 9900, interval: 'month', programs: ['yoga'], blockWhenPastDue: false, active: true,
  },
];

const NAMES = [
  'Alex Rivera', 'Jordan Nakamura', 'Sofia Adeyemi', 'Liam Kowalski', 'Nina Petrova',
  'Marcus Chen', 'Yusuf Demir', 'Elena Vasquez', 'Tobias Lindqvist', 'Amara Diallo',
  'Ravi Krishnan', 'Cleo Marchetti', 'Kai Thompson', 'Freya Andersen',
];

export const members = NAMES.map((name, i) => ({
  id: `mem_${i + 1}`,
  orgId: 'org_1',
  locationId: 'loc_1',
  name,
  email: `${name.split(' ')[0].toLowerCase()}${i + 1}@example.test`,
  phone: `+1555${String(1000000 + i * 7717).slice(0, 7)}`,
  joinedOn: addDays(today, -(20 + i * 23)),
  status: i === 12 ? 'lapsed' : i === 13 ? 'lead' : 'active',
  emergencyContact: i % 3 === 0 ? { name: 'Listed on file', phone: '+15555550100' } : null,
  waiverSignedAt: i === 13 ? null : `${addDays(today, -(20 + i * 23))}T14:02:00.000Z`,
  notes: '',
}));

const membershipFor = (memberId, planId, extra = {}) => ({
  id: `sub_${memberId}`,
  orgId: 'org_1',
  memberId,
  planId,
  status: 'active',
  startDate: addDays(today, -60),
  currentPeriodStart: monthStart,
  currentPeriodEnd: monthEnd,
  interval: 'month',
  freezes: [],
  usageDates: [],
  creditsRemaining: 0,
  creditsExpireOn: null,
  pastDueCents: 0,
  ...extra,
});

export const memberships = [
  membershipFor('mem_1', 'plan_unlimited', { amountCents: 17900 }),
  membershipFor('mem_2', 'plan_3x', { amountCents: 13900, usageDates: [addDays(today, -1), addDays(today, -2)] }),
  membershipFor('mem_3', 'plan_unlimited', { amountCents: 17900 }),
  membershipFor('mem_4', 'plan_pack10', {
    creditsRemaining: 6, amountCents: 0, creditsExpireOn: addDays(today, 60),
  }),
  membershipFor('mem_5', 'plan_unlimited', { amountCents: 17900, pastDueCents: 17900 }),
  membershipFor('mem_6', 'plan_3x', { amountCents: 13900 }),
  membershipFor('mem_7', 'plan_yoga', { amountCents: 9900 }),
  membershipFor('mem_8', 'plan_unlimited', {
    amountCents: 17900,
    freezes: [{ from: addDays(today, -5), to: addDays(today, 25) }],
  }),
  membershipFor('mem_9', 'plan_pack10', {
    creditsRemaining: 0, amountCents: 0, creditsExpireOn: addDays(today, 14),
  }),
  membershipFor('mem_10', 'plan_unlimited', { amountCents: 17900 }),
  membershipFor('mem_11', 'plan_3x', { amountCents: 13900 }),
  // Paid for, never used, and now out of time — the case that silently worked
  // forever before credit expiry was enforced.
  membershipFor('mem_12', 'plan_dropin', {
    creditsRemaining: 1, amountCents: 0, creditsExpireOn: addDays(today, -3),
  }),
  membershipFor('mem_13', 'plan_unlimited', { amountCents: 17900, status: 'cancelled' }),
];

export const templates = [
  {
    id: 'tpl_wod_am', orgId: 'org_1', locationId: 'loc_1', name: 'CrossFit — 6am',
    program: 'crossfit', weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'], time: '06:00',
    durationMin: 60, capacity: 16, startDate: addDays(today, -120),
    timeZone: 'America/Chicago', staffId: 'staff_2', roomId: 'room_floor',
    exceptions: [], overrides: {},
  },
  {
    id: 'tpl_wod_pm', orgId: 'org_1', locationId: 'loc_1', name: 'CrossFit — 5:30pm',
    program: 'crossfit', weekdays: ['mon', 'tue', 'wed', 'thu'], time: '17:30',
    durationMin: 60, capacity: 20, startDate: addDays(today, -120),
    timeZone: 'America/Chicago', staffId: 'staff_3', roomId: 'room_floor',
    exceptions: [], overrides: {},
  },
  {
    id: 'tpl_yoga', orgId: 'org_1', locationId: 'loc_1', name: 'Recovery Yoga',
    program: 'yoga', weekdays: ['tue', 'thu', 'sun'], time: '19:00',
    // Ten mats in Studio B, which is what makes it fill and hold a waitlist.
    durationMin: 45, capacity: 10, startDate: addDays(today, -120),
    timeZone: 'America/Chicago', staffId: 'staff_3', roomId: 'room_studio',
    exceptions: [], overrides: {},
  },
  {
    id: 'tpl_lift', orgId: 'org_1', locationId: 'loc_1', name: 'Barbell Club',
    program: 'strength', weekdays: ['sat'], time: '09:00',
    durationMin: 90, capacity: 10, startDate: addDays(today, -120),
    timeZone: 'America/Chicago', staffId: 'staff_2', roomId: 'room_platform',
    exceptions: [], overrides: {},
  },
];

export const rooms = [
  { id: 'room_floor', name: 'Main floor', capacity: 24 },
  { id: 'room_studio', name: 'Studio B', capacity: 14 },
  { id: 'room_platform', name: 'Lifting platforms', capacity: 10 },
];

export const policy = {
  opensHoursBefore: 336,
  closesMinutesBefore: 0,
  waitlist: true,
  waitlistCap: 8,
  lateCancelMinutes: 720,
  lateCancelFeeCents: 1000,
  noShowFeeCents: 1500,
  lateCancelReturnsCredit: false,
};

// Bookings across the week, not just today.
//
// The demo IS the pitch, so the seed has to show the product doing the things
// that are hard: a class filled to capacity with a live waitlist, a member
// already checked in, and enough spread that the week-ahead strip has a shape
// instead of one bar. A gym where only today has any bookings looks like a
// gym nobody goes to.
const bookedAt = (daysAgo, hhmm) => `${addDays(today, -daysAgo)}T${hhmm}:00.000Z`;

let bookingSeq = 0;
const seat = (occurrenceId, memberId, extra = {}) => ({
  id: `bk_${(bookingSeq += 1)}`,
  orgId: 'org_1',
  occurrenceId,
  memberId,
  membershipId: `sub_${memberId}`,
  status: 'booked',
  consumedCredit: false,
  consumedUsage: false,
  bookedAt: bookedAt(1, '15:00'),
  ...extra,
});

// Recovery Yoga is full tonight with two people waiting — the state that
// exercises promotion, credit accounting and the waitlist UI at once, and the
// one thing the demo could not show before.
//
// The two waiting deliberately cannot pay for a seat: one has spent their pack
// and one's credits expired. Promoting them must be refused, which is exactly
// the case the write path has to re-check server-side rather than trust.
const yogaToday = `tpl_yoga:${today}`;
const YOGA_ROSTER = ['mem_7', 'mem_1', 'mem_2', 'mem_3', 'mem_5',
  'mem_6', 'mem_8', 'mem_10', 'mem_11', 'mem_4'];

export const bookings = [
  // This morning: one already checked in, the rest still open.
  seat(`tpl_wod_am:${today}`, 'mem_1', { status: 'attended', checkedInAt: bookedAt(0, '11:02') }),
  seat(`tpl_wod_am:${today}`, 'mem_2', { consumedUsage: true }),
  seat(`tpl_wod_am:${today}`, 'mem_4', { consumedCredit: true, bookedAt: bookedAt(1, '16:00') }),
  seat(`tpl_wod_am:${today}`, 'mem_10'),
  seat(`tpl_wod_am:${today}`, 'mem_11', { consumedUsage: true }),

  seat(`tpl_wod_pm:${today}`, 'mem_3'),
  seat(`tpl_wod_pm:${today}`, 'mem_6', { consumedUsage: true }),
  seat(`tpl_wod_pm:${today}`, 'mem_10'),

  // Tonight's yoga: full, with a waitlist behind it.
  ...YOGA_ROSTER.map((m) => seat(yogaToday, m, {
    consumedCredit: m === 'mem_4',
    consumedUsage: ['mem_2', 'mem_6', 'mem_11'].includes(m),
  })),
  seat(yogaToday, 'mem_9', { status: 'waitlisted', waitlistPosition: 1 }),
  seat(yogaToday, 'mem_12', { status: 'waitlisted', waitlistPosition: 2 }),

  // The rest of the week, thinning out the further ahead it gets — which is
  // what a real booking curve looks like.
  ...['mem_1', 'mem_3', 'mem_6', 'mem_10'].map((m) => seat(`tpl_wod_am:${addDays(today, 1)}`, m)),
  ...['mem_2', 'mem_11'].map((m) => seat(`tpl_wod_pm:${addDays(today, 1)}`, m, { consumedUsage: true })),
  ...['mem_1', 'mem_7', 'mem_8'].map((m) => seat(`tpl_yoga:${addDays(today, 2)}`, m)),
  ...['mem_3', 'mem_10'].map((m) => seat(`tpl_wod_am:${addDays(today, 2)}`, m)),
  ...['mem_1', 'mem_6'].map((m) => seat(`tpl_wod_pm:${addDays(today, 3)}`, m)),
  seat(`tpl_wod_am:${addDays(today, 4)}`, 'mem_10'),
];

export const invoices = [
  { id: 'inv_1', orgId: 'org_1', memberId: 'mem_5', totalCents: 17900, status: 'past_due', issuedOn: addDays(today, -9), failedOnKey: addDays(today, -9), attemptsMade: 2, currency: 'USD' },
  { id: 'inv_2', orgId: 'org_1', memberId: 'mem_9', totalCents: 19000, status: 'past_due', issuedOn: addDays(today, -3), failedOnKey: addDays(today, -3), attemptsMade: 1, currency: 'USD' },
  // Past due with no recorded failure date. Reaching past_due without one is
  // real — a manual status change, an import, a webhook that arrived without
  // it — and it used to crash the whole Billing screen. Seeded so the
  // needs-review path is demonstrable rather than theoretical.
  { id: 'inv_3', orgId: 'org_1', memberId: 'mem_11', totalCents: 13900, status: 'past_due', issuedOn: addDays(today, -6), failedOnKey: null, attemptsMade: 0, currency: 'USD' },
  { id: 'inv_4', orgId: 'org_1', memberId: 'mem_1', totalCents: 17900, status: 'paid', issuedOn: monthStart, paidOn: monthStart, currency: 'USD' },
  { id: 'inv_5', orgId: 'org_1', memberId: 'mem_3', totalCents: 17900, status: 'paid', issuedOn: monthStart, paidOn: monthStart, currency: 'USD' },
  { id: 'inv_6', orgId: 'org_1', memberId: 'mem_2', totalCents: 13900, status: 'paid', issuedOn: monthStart, paidOn: monthStart, currency: 'USD' },
];

export const seed = {
  org, locations, staff, plans, members, memberships,
  templates, rooms, policy, bookings, invoices,
};
